import * as React from 'react';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

/** Material UI Packages. */
import { Dialog, Box } from '@mui/material';

/** Redux Packages. */
import { useSelector, useDispatch } from 'react-redux';

/** Library for optimizing performance. */
import { debounce } from 'lodash';

/** Internal Components. */
import { PaperComponent } from 'shared/ui/DraggableDialog';
import DialogHeader from 'shared/ui/DialogHeader';
import InputField, { DateField } from 'shared/ui/InputForm';
import UpdateRateInfoTable from './UpdateRateInfoTable';
import { updateSocketData } from 'shared/lib/fetchSocket';

/** Per shipment type (tabValue) configs for the parent-row update. */
const SHIPMENT_CONFIG = {
    0: { entity: 'il_ocean_import', reducer: 'setOimMainUpdateData', formKey: 'oimMainUpdateForm' },
    1: { entity: 'il_ocean_export', reducer: 'setOexMainUpdateData', formKey: 'oexMainUpdateForm' },
    2: { entity: 'il_air_import', reducer: 'setAimMainUpdateData', formKey: 'aimMainUpdateForm' },
    3: { entity: 'il_air_export', reducer: 'setAexMainUpdateData', formKey: 'aexMainUpdateForm' },
    4: { entity: 'il_ftl_ltl', reducer: 'setTruckingMainUpdateData', formKey: 'truckingMainUpdateForm' },
};

export default function UpdateRateInfo({ row, rateOpen, setRateOpen }) {
    const dispatch = useDispatch();
    const tabValue = useSelector((state) => state.tabValueForm);
    const cfg = SHIPMENT_CONFIG[tabValue] ?? null;

    const mainData = useSelector((state) => (cfg ? state[cfg.formKey]?.find((item) => item.id === row.id) : null));

    /** Local state for the quote number text field (debounced send). */
    const [quoteNumber, setQuoteNumber] = useState('');

    /** True while the quote number field is focused - blocks socket echo from overwriting typing. */
    const isEditingQuoteNumberRef = useRef(false);

    /** Sync quote_number local state when dialog opens or Redux value changes. */
    useEffect(() => {
        if (rateOpen && !isEditingQuoteNumberRef.current) {
            setQuoteNumber(mainData?.quote_number ?? '');
        }
    }, [rateOpen, mainData?.quote_number]);

    const sendUpdate = useCallback(
        (key, value) => {
            if (!cfg) return;
            updateSocketData(dispatch, tabValue, 'cell', cfg.entity, cfg.reducer, {
                id: row.id,
                key,
                value: value ?? null,
            });
        },
        [dispatch, tabValue, cfg, row.id]
    );

    const debouncedQuote = useMemo(() => debounce((v) => sendUpdate('quote_number', v), 500), [sendUpdate]);

    useEffect(() => {
        return () => {
            debouncedQuote.flush();
        };
    }, [debouncedQuote]);

    const handleClose = () => {
        debouncedQuote.flush();
        isEditingQuoteNumberRef.current = false;
        setRateOpen(false);
    };

    return (
        <Dialog
            maxWidth="xl"
            fullWidth={true}
            open={rateOpen}
            onClose={handleClose}
            PaperComponent={PaperComponent}
            PaperProps={{ sx: { borderRadius: '18px', height: '75vh' } }}
        >
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <DialogHeader
                    cursor
                    title={'Rate Info'}
                    onClose={handleClose}
                />

                {cfg && (
                    <Box sx={{ display: 'flex', gap: 2, px: 1, pt: 2, justifyContent: 'flex-end' }}>
                        <InputField
                            label="Quote Number"
                            name="quote_number"
                            value={quoteNumber}
                            maxLength={30}
                            onChange={(e) => {
                                const next = e.target.value.slice(0, 30);
                                setQuoteNumber(next);
                                debouncedQuote(next);
                            }}
                            onFocus={() => {
                                isEditingQuoteNumberRef.current = true;
                            }}
                            onBlur={() => {
                                isEditingQuoteNumberRef.current = false;
                                debouncedQuote.flush();
                            }}
                            labelStyle={{ fontSize: 13, width: 'auto', whiteSpace: 'nowrap' }}
                            inputStyle={{ width: 200, height: '27px', fontSize: '14px' }}
                            sx={{ gap: 1 }}
                        />
                        <DateField
                            label="Gate-In Date"
                            name="gatein_date"
                            value={mainData?.gatein_date ?? ''}
                            onChange={(e) =>
                                sendUpdate('gatein_date', e.target.value ? e.target.value.format('YYYY-MM-DD') : null)
                            }
                            labelStyle={{ fontSize: 13, width: 'auto', whiteSpace: 'nowrap' }}
                            width={140}
                            sx={{ gap: 1, '& .MuiInputBase-input': { fontSize: '14px' } }}
                        />
                    </Box>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'stretch',
                        justifyContent: 'center',
                        flex: 1,
                        minHeight: 0,
                        gap: 1,
                        px: 1,
                        pb: 1,
                    }}
                >
                    <UpdateRateInfoTable row={row} header={'NET'} />
                    <UpdateRateInfoTable row={row} header={'SELLING'} />
                </Box>
            </Box>
        </Dialog>
    );
}
